import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { CHART_COLORS, getAgChartTheme, getChartColors, formatNumber, formatPercent } from './chartTheme';
import { useTheme } from '../../../stores/ThemeContext';

/**
 * @typedef {Object} Trade
 * @property {number} profit - Trade profit/loss
 * @property {string} [symbol] - Trading symbol (e.g., 'EUR/USD')
 * @property {string} [direction] - Trade direction ('BUY' or 'SELL')
 * @property {string} [asset_type] - Asset type ('forex', 'crypto', etc.)
 * @property {string} [orderType] - Order type ('market', 'limit', etc.)
 */

/**
 * @typedef {'symbol' | 'direction' | 'assetType' | 'orderType'} GroupByOption
 */

/**
 * @typedef {Object} BarDataPoint
 * @property {string} category - Category name
 * @property {number} wins - Number of winning trades
 * @property {number} losses - Number of losing trades
 * @property {number} total - Total trades in category
 * @property {number} winRate - Win rate percentage
 * @property {number} avgProfit - Average profit for category
 */

/**
 * Breakeven threshold for categorizing trades
 */
const BREAKEVEN_THRESHOLD = 0.01;

/**
 * Maps groupBy prop values to trade object keys
 */
const GROUP_BY_MAP = {
  symbol: 'symbol',
  direction: 'direction',
  assetType: 'asset_type',
  orderType: 'orderType',
};

/**
 * Display labels for groupBy options
 */
const GROUP_BY_LABELS = {
  symbol: 'Symbol',
  direction: 'Direction',
  assetType: 'Asset Type',
  orderType: 'Order Type',
};

/**
 * Wins vs Losses by Category Bar Chart
 * Reusable component that accepts a groupBy prop for different categorizations
 *
 * @param {Object} props
 * @param {Trade[]} props.trades - Array of trade objects
 * @param {GroupByOption} [props.groupBy='symbol'] - Category to group by
 * @param {string} [props.title] - Chart title (auto-generated if not provided)
 * @param {number} [props.height=350] - Chart height in pixels
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {number} [props.maxCategories=10] - Maximum categories to display
 *
 * @example
 * // Group by symbol
 * <CategoryBarChart trades={trades} groupBy="symbol" />
 *
 * @example
 * // Group by direction with custom title
 * <CategoryBarChart
 *   trades={trades}
 *   groupBy="direction"
 *   title="Long vs Short Performance"
 *   height={400}
 * />
 *
 * @example
 * // Group by asset type
 * <CategoryBarChart trades={trades} groupBy="assetType" />
 *
 * @example
 * // Group by order type
 * <CategoryBarChart trades={trades} groupBy="orderType" />
 */
const CategoryBarChart = ({
  trades = [],
  groupBy = 'symbol',
  title,
  height = 350,
  className = '',
  maxCategories = 10,
}) => {
  const { isDark } = useTheme();
  const chartColors = getChartColors(isDark);
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height });

  // Handle responsive resizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setDimensions({ width, height });
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [height]);

  // Compute chart data grouped by category
  const chartData = useMemo(() => {
    if (!trades || trades.length === 0) {
      return [];
    }

    const groupKey = GROUP_BY_MAP[groupBy] || 'symbol';
    const grouped = {};

    // Group trades by category
    trades.forEach((trade) => {
      const category = trade[groupKey] || 'Unknown';
      if (!grouped[category]) {
        grouped[category] = {
          category,
          wins: 0,
          losses: 0,
          total: 0,
          totalProfit: 0,
        };
      }

      const profit = Number(trade.profit) || 0;
      grouped[category].total++;
      grouped[category].totalProfit += profit;

      if (profit > BREAKEVEN_THRESHOLD) {
        grouped[category].wins++;
      } else if (profit < -BREAKEVEN_THRESHOLD) {
        grouped[category].losses++;
      }
    });

    // Convert to array and calculate derived values
    /** @type {BarDataPoint[]} */
    let data = Object.values(grouped).map((group) => ({
      category: group.category,
      wins: group.wins,
      losses: group.losses,
      total: group.total,
      winRate: group.total > 0 ? (group.wins / group.total) * 100 : 0,
      avgProfit: group.total > 0 ? group.totalProfit / group.total : 0,
    }));

    // Sort by total trades descending and limit
    data.sort((a, b) => b.total - a.total);
    data = data.slice(0, maxCategories);

    return data;
  }, [trades, groupBy, maxCategories]);

  // Generate chart title
  const chartTitle = title || `Wins vs Losses by ${GROUP_BY_LABELS[groupBy] || 'Category'}`;

  // Chart options
  const options = useMemo(() => ({
    theme: getAgChartTheme(isDark),
    data: chartData,
    title: {
      text: chartTitle,
      fontSize: 16,
      fontWeight: 600,
      color: chartColors.text,
    },
    series: [
      {
        type: 'bar',
        xKey: 'category',
        yKey: 'wins',
        yName: 'Wins',
        fill: CHART_COLORS.win,
        stroke: CHART_COLORS.win,
        stacked: true,
        tooltip: {
          renderer: ({ datum }) => ({
            title: `${datum.category} - Wins`,
            content: `Trades: ${datum.wins}\nWin Rate: ${formatPercent(datum.winRate)}\nAvg Profit: ${formatNumber(datum.avgProfit)}`,
          }),
        },
        highlightStyle: {
          item: {
            fillOpacity: 0.8,
          },
        },
      },
      {
        type: 'bar',
        xKey: 'category',
        yKey: 'losses',
        yName: 'Losses',
        fill: CHART_COLORS.loss,
        stroke: CHART_COLORS.loss,
        stacked: true,
        tooltip: {
          renderer: ({ datum }) => ({
            title: `${datum.category} - Losses`,
            content: `Trades: ${datum.losses}\nWin Rate: ${formatPercent(datum.winRate)}\nAvg Profit: ${formatNumber(datum.avgProfit)}`,
          }),
        },
        highlightStyle: {
          item: {
            fillOpacity: 0.8,
          },
        },
      },
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        title: {
          text: GROUP_BY_LABELS[groupBy] || 'Category',
          color: chartColors.textMuted,
          fontSize: 12,
        },
        label: {
          color: chartColors.textMuted,
          fontSize: 11,
          rotation: chartData.length > 6 ? 45 : 0,
        },
        line: {
          stroke: CHART_COLORS.axis,
        },
        gridLine: {
          style: [{
            stroke: CHART_COLORS.gridLine,
            lineDash: [4, 2],
          }],
        },
      },
      {
        type: 'number',
        position: 'left',
        title: {
          text: 'Trade Count',
          color: chartColors.textMuted,
          fontSize: 12,
        },
        label: {
          color: chartColors.textMuted,
          fontSize: 11,
        },
        line: {
          stroke: CHART_COLORS.axis,
        },
        gridLine: {
          style: [{
            stroke: CHART_COLORS.gridLine,
            lineDash: [4, 2],
          }],
        },
      },
    ],
    legend: {
      enabled: true,
      position: 'bottom',
      item: {
        marker: {
          shape: 'square',
          size: 12,
        },
        label: {
          color: chartColors.text,
          fontSize: 12,
        },
        paddingX: 16,
        paddingY: 8,
      },
    },
    background: {
      fill: CHART_COLORS.background,
    },
  }), [chartData, chartTitle, groupBy, isDark]);

  // Empty state
  if (!trades || trades.length === 0) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">No trade data</p>
          <p className="text-sm">Add trades to see category breakdown</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`w-full ${className}`} style={{ height }}>
      <AgCharts ref={chartRef} options={options} />
    </div>
  );
};

export default CategoryBarChart;
